import { useEffect, useState } from "react";
import { ScrollText } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

const ACTION_COLOR = {
  create: "border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10",
  update: "border-blue-500/30 text-blue-600 dark:text-blue-400 bg-blue-500/10",
  delete: "border-destructive/30 text-destructive bg-destructive/10",
  login: "border-muted text-muted-foreground bg-muted/40",
  logout: "border-muted text-muted-foreground bg-muted/40",
  duplicate: "border-amber-500/30 text-amber-600 dark:text-amber-400 bg-amber-500/10",
};

export default function AuditLogs() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const { data } = await api.get("/audit", { params: { limit: 300 } });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <p className="kpi-label">{t("nav.audit")}</p>
        <h1 className="text-2xl font-bold tracking-tight">{t("audit.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("audit.subtitle")}</p>
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit.time")}</TableHead>
                <TableHead>{t("audit.actor")}</TableHead>
                <TableHead>{t("audit.action")}</TableHead>
                <TableHead>{t("audit.target")}</TableHead>
                <TableHead>{t("audit.details")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t("common.loading")}</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground"><ScrollText className="h-5 w-5 mx-auto mb-2" />{t("audit.no_events")}</TableCell></TableRow>
              ) : (
                items.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="text-xs font-mono whitespace-nowrap">
                      {new Date(a.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{a.actor_email || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`uppercase tracking-wider text-[10px] ${ACTION_COLOR[a.action] || ""}`}>
                        {a.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{a.target_type} {a.target_id ? <span className="font-mono opacity-60 ml-1">{a.target_id.slice(0, 14)}</span> : null}</TableCell>
                    <TableCell className="text-xs font-mono max-w-[320px] truncate">
                      {a.details ? JSON.stringify(a.details) : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
